import React, { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Divider, IconButton, Paper, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import GoogleIcon from './Components/GoogleLogin/Icons';
import MicrosoftIcon from './Components/MicrosoftLogin/Icons';
import CustomButton from '../../Components/Inputs/CustomButton';
import FilledLogo from '../../Components/Logos/FilledLogo';
import { CenteredFlexBox, ColumnFlexBox } from '../../Components/FlexBox';

// Diagonal brand gradient from the mockup (screen 1 · Accesso).
const LOGIN_GRADIENT =
  'linear-gradient(150deg, #4aa15a 0%, #2f8a6e 42%, #1f6a9e 78%, #164e86 100%)';

// Scoped overrides that compact the shared FirstLogin widget for mobile: bold
// title, grey subtitle tucked under it, tight fields.
const COMPACT_WIDGET = {
  '& .MuiTypography-h4': {
    fontSize: '24px !important',
    fontWeight: '700 !important',
    mb: 0,
  },
  '& .MuiTypography-subtitle1': {
    fontSize: '13px !important',
    color: '#94a3b8 !important',
    mt: '-6px !important',
    mb: '10px !important',
  },
  // The field captions. The mockup's dark grey is for the white sheet: on the dark one it vanished,
  // so the dark theme reads its own secondary text colour.
  '& .MuiTypography-subtitle2': {
    fontSize: '13px !important',
    color: (theme) =>
      theme.palette.mode === 'dark'
        ? `${theme.palette.text.secondary} !important`
        : '#3a3a42 !important',
  },
  '& .MuiTextField-root': { mb: '2px !important' },
  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
  '& .MuiOutlinedInput-input': { padding: '11px 14px !important' },
};

// Fixed square buttons so passkey + Google render identically. GoogleIcon is a
// 48px inline-styled <div><svg>, so its children are forced down with !important.
const SSO_ICON_BTN = {
  width: 46,
  height: 46,
  p: 0,
  border: '1px solid rgba(0,0,0,.16)',
  borderRadius: '12px',
  '& > div': {
    width: '24px !important',
    height: '24px !important',
    float: 'none !important',
  },
  '& svg': { width: '24px !important', height: '24px !important' },
};

/**
 * MobileLoginFormPage — the mockup login (brand block over a white sheet).
 * Reuses the exact desktop auth flow: react-hook-form + reCAPTCHA + onSubmit,
 * passwordless (passkey) and external (Google/Microsoft) login. Scrollbar is
 * hidden (`.scr`) so the page stays clean while remaining reachable.
 */
const MobileLoginFormPage = function MobileLoginFormPage({
  widget,
  onSubmit,
  needToChangePassword = false,
  loginInPending = false,
}) {
  const intl = useIntl();
  const methods = useForm();
  const { API_URL, PROVIDER, AUTH_SCHEME, RECAPTCHA } = window.databeasy || {};

  const recaptchaRef = useRef();
  const [loginActive, setLoginActive] = useState(false);

  const redirectURI = `${window.location.protocol}//${window.location.host}/`;
  const googleLoginUrl = `${API_URL}Auth/externalLogin?provider=Google&authScheme=${AUTH_SCHEME}&returnUrl=${redirectURI}`;
  const microsoftLoginUrl = `${API_URL}Auth/externalLogin?provider=Microsoft&authScheme=${AUTH_SCHEME}&returnUrl=${redirectURI}`;

  // A reCAPTCHA token is single-use: reset it once read, so a second click needs a new one.
  const onRecaptchaVerifySubmit = (data) => {
    onSubmit(data, recaptchaRef.current.getValue());
    recaptchaRef.current.reset();
    setLoginActive(false);
  };

  return (
    <ColumnFlexBox
      className="scr"
      sx={{
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: LOGIN_GRADIENT,
      }}
    >
      <ColumnFlexBox sx={{ flex: 'none', px: 3, pt: 3.5, pb: 1, gap: 1 }}>
        <FilledLogo width="230px" height="60px" />
        {intl.messages.SLOGAN.split(',').map((line, index) => (
          <Typography
            key={index}
            sx={{
              color: '#fff',
              fontSize: 17,
              fontWeight: 500,
              lineHeight: 1.2,
              whiteSpace: 'pre-wrap',
            }}
          >
            {line}
          </Typography>
        ))}
      </ColumnFlexBox>

      <ColumnFlexBox sx={{ flex: 1 }} />

      <Paper
        elevation={8}
        sx={{
          flex: 'none',
          borderRadius: '24px 24px 0 0',
          px: 2.5,
          pt: 2.5,
          pb: 'calc(env(safe-area-inset-bottom) + 16px)',
          ...COMPACT_WIDGET,
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onRecaptchaVerifySubmit)}>
            <ColumnFlexBox sx={{ gap: 1 }}>
              {widget}
              <CenteredFlexBox
                sx={{
                  justifyContent: 'center',
                  transform: 'scale(0.92)',
                  my: -0.5,
                }}
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA}
                  onChange={() => setLoginActive(true)}
                />
              </CenteredFlexBox>
              <CustomButton
                type="submit"
                fullWidth
                data-testid="login-submit"
                styles={{ height: 46 }}
                disabled={!loginActive || loginInPending}
                label={intl.messages.LOGIN}
              />
            </ColumnFlexBox>
          </form>
        </FormProvider>

        {!needToChangePassword && (
          <>
            <Divider sx={{ mt: 1.25, mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#a2a2a2' }}>
                {intl.messages.OR}
              </Typography>
            </Divider>
            <CenteredFlexBox sx={{ justifyContent: 'center', gap: 1.5 }}>
              <IconButton
                aria-label={intl.messages.LOGIN_PASSKEY}
                onClick={() => onSubmit('passkey')}
                sx={SSO_ICON_BTN}
              >
                <KeyRoundedIcon />
              </IconButton>
              {PROVIDER === 'GOOGLE' && (
                <form action={googleLoginUrl} method="post">
                  <IconButton
                    type="submit"
                    aria-label={intl.messages.GOOGLELOGIN}
                    sx={SSO_ICON_BTN}
                  >
                    <GoogleIcon />
                  </IconButton>
                </form>
              )}
              {PROVIDER === 'MICROSOFT' && (
                <form action={microsoftLoginUrl} method="post">
                  <IconButton
                    type="submit"
                    aria-label={intl.messages.MICROSOFTLOGIN}
                    sx={SSO_ICON_BTN}
                  >
                    <MicrosoftIcon />
                  </IconButton>
                </form>
              )}
            </CenteredFlexBox>
          </>
        )}
      </Paper>
    </ColumnFlexBox>
  );
};

export default MobileLoginFormPage;
